<template>
    <div class="page-audit audit-edit layout-max-width">

        <div class="heading mb-4 d-flex flex-column flex-lg-row flex-wrap justify-content-between align-items-center">
            <h1>
                Audit for {{ projectName }}
                <template v-if="auditIsOpen">
                    - Started
                    <timestamp
                        class="d-inline-block"
                        :date="startDate"
                        :include-time="false"/>
                </template>
                <template v-else>
                    - Completed
                    <timestamp
                        class="d-inline-block"
                        :date="completedDate"
                        :include-time="false"/>
                </template>
            </h1>

            <div class="heading__buttons" v-if="canEdit">
                <b-button class="btn btn-primary btn-lg"
                          v-if="auditIsOpen"
                          @click="createAuditEquipment">
                    Add Equipment
                    <b-icon-plus-square/>
                </b-button>

                <b-button v-if="auditIsOpen" class="btn btn-lg ml-2"
                          :disabled="!readyToCompleteAudit"
                          :variant="completeVariant"
                          @click="completeAudit">
                    Complete Audit
                    <b-icon-bag-check/>
                </b-button>
                <b-button v-else class="btn btn-secondary btn-lg ml-2"
                          variant="primary"
                          @click="reopenAudit">
                    Re-Open Audit
                    <b-icon-arrow-counterclockwise/>
                </b-button>
            </div>
        </div>

        <data-table v-if="audit"
                    class="search-max-width table-responsive"
                    ref="dataTable"
                    :show-filters="canView"
                    :show-pager="false"
                    :get-items="getEntries"
                    v-model="filters"
                    :fields="fields">
            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'actions'" class="action-buttons d-flex">
                    <auth-template model="audit-equipment" action="edit" :arg="slotProps.row.item.id">
                        <router-link
                            class="btn btn-secondary btn-sm mr-2 btn-icon"
                            :to="`/auditEquipment/edit/${auditId}/${slotProps.row.item.id}`">
                            <b-icon-pencil font-scale="1"/>
                        </router-link>
                    </auth-template>
                    <auth-template model="audit-equipment" action="view" :arg="slotProps.row.item.id">
                        <router-link
                            class="btn btn-secondary btn-sm mr-2 btn-icon"
                            :to="`/auditEquipment/view/${auditId}/${slotProps.row.item.id}`">
                            <b-icon-eye font-scale="1"/>
                        </router-link>
                    </auth-template>
                    <auth-template model="audit-equipment" action="delete" :arg="slotProps.row.item.id">
                        <div @click="deleteAuditEquipment(slotProps.row.item)"
                             class="btn btn-danger btn-sm mr-2 btn-icon">
                            <b-icon-trash font-scale="1"/>
                        </div>
                    </auth-template>
                </div>
                <div v-if="slotProps.row.field.key === 'equipment'">
                    {{ equipmentString(slotProps.row.item) }}
                </div>
                <div v-if="slotProps.row.field.key === 'company'">
                    {{ getName(slotProps.row.item.company) }}
                </div>
                <div v-if="slotProps.row.field.key === 'status'">
                    <audit-equipment-status-badge :audit-equipment="slotProps.row.item"/>
                </div>
                <div v-if="slotProps.row.field.key === 'responseStatus'">
                    <!--                                        <timestamp :date="slotProps.row.item.responseStatus"/>-->
                    <audit-equipment-response-badge
                        :audit-equipment="slotProps.row.item"/>
                </div>
            </template>
        </data-table>

        <div v-if="canEdit" class="audit-history mt-3">
            <b-card-header>
                <h3>History</h3>
                <helper tag="audit-edit-history"/>
            </b-card-header>
            <b-card-body>
                <log-entries table="audit" :model-instance-id="auditId"/>
            </b-card-body>
        </div>
    </div>

</template>

<script>
import Authorization from '@/providers/Authorization'
import Api from '@/providers/Api'

export default {
    name: 'AuditEdit',
    props: {
        auditId: Number,
    },
    data () {
        return {
            audit: null,

            fields: [
                { key: 'equipment', sortable: true, label: 'Equipment' },
                { key: 'company', sortable: true, label: 'Company' },
                { key: 'status', sortable: false, label: 'Status' },
                { key: 'responseStatus', sortable: false, label: 'Response Status' },
                { key: 'actions', sortable: false, label: 'Actions' },
            ],
            canEdit: false,
            canView: false,
        }
    },
    computed: {
        filters () {
            //Hide the company filter for non admins and just filter by their own company
            //NB: this is only working with filter persistence turned off
            let companyId = this.$user.getCompanyId()
            let type = this.canEdit ? 'multiselect' : 'hidden'

            return {
                company_id: {
                    field: 'company_id',
                    type: type,
                    search: (query) => {
                        return Api.getItems(this, 'company', { name: { field: 'name', value: query } })
                    },
                    apiTable: 'company',
                    labelField: 'name',
                    value: companyId,
                    label: 'Company'
                },
                equipment: {
                    field: 'equipment',
                    label: 'Equipment',
                    type: 'string',
                    value: '',
                },
                status: {
                    field: 'status',
                    label: 'Status',
                    type: 'select',
                    options: [
                        { value: 'NO_ISSUE', text: 'No Issues' },
                        { value: 'ISSUE', text: 'Issues' },
                        { value: 'VIOLATION', text: 'Violation' },
                        { value: 'RESOLVED', text: 'Resolved' },
                    ]
                },
                responseStatus: {
                    field: 'responseStatus',
                    label: 'Response Status',
                    type: 'select',
                    options: [
                        { value: 'AWAITING', text: 'Awaiting Response' },
                        { value: 'LATE', text: 'Late' },
                        { value: 'RESPONDED', text: 'Responded' },
                        { value: 'NO_FOLLOW_UP', text: 'No Follow Up' },
                    ]
                }
            }
        },
        projectName () {
            return this.audit && this.audit.project.name ? this.audit.project.name : ''
        },
        readyToCompleteAudit () {
            return this.audit && this.audit.readyToComplete
        },
        completedDate () {
            return this.audit && this.audit.completed_date
        },
        startDate () {
            return this.audit && this.audit.start_date
        },
        auditIsOpen () {
            return !this.completedDate
        },
        completeVariant () {
            return this.readyToCompleteAudit ? 'success' : 'success'
        }
    },
    methods: {
        createAuditEquipment () {
            this.$router.push(`/auditEquipment/create/${this.auditId}`)
        },
        equipmentString (item) {
            return item.equipment ? item.equipment.vehicle_id : ''
        },
        getName (item) {
            return item ? item.name : ''
        },
        completeAudit () {
            this.$api.completeAudit(this, this.auditId).then((response) => {
                this.$router.push('/project/list')
            }, (error) => {
                this.$toasted.show('Could not complete audit')
            })
        },
        reopenAudit () {
            this.$api.reopenAudit(this, this.auditId).then((response) => {
                this.fetchAudit()
                this.$toasted.show('Audit Re-Opened!')
            }, (error) => {
                this.$toasted.show('Could not re-open audit')
            })
        },
        fetchAudit () {
            this.$api.getItem(this, 'audit', this.auditId, ['project', 'auditEquipments', 'auditEquipments.equipment']).then((audit) => {
                this.audit = audit
            })
        },
        getEntries () {
            this.filters.audit_id = { field: 'audit_id', type: 'hidden', value: this.auditId }
            return this.$api.getItems(this, 'audit-equipment', this.filters, {}, ['equipment']).then((auditEquipments) => {
                return auditEquipments
            })
        },
        deleteAuditEquipment (item) {
            this.$bvModal.msgBoxConfirm('Would you like to remove this Equipment from this Audit? This action cannot be undone.')
                .then(async value => {
                    if (value) {
                        await this.$api.deleteItem(this, 'audit-equipment', item)
                        this.$refs.dataTable.refresh()
                    }
                })
        },
    },
    created () {
        this.fetchAudit()
        Authorization.can({ action: 'edit', model: 'audit', arg: this.auditId })
            .then((canEdit) => {
                this.canEdit = canEdit
            })
        Authorization.can({ action: 'view', model: 'audit', arg: this.auditId })
            .then((canView) => {
                this.canView = canView
            })
    },
}
</script>

<style lang="scss">
</style>